import { Box, Paper, Skeleton, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import { formatInTimeZone } from 'date-fns-tz'
import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Notification } from '@components/prereg_validation/notification'
import { useLocationContext } from '@providers/location/location_provider'
import { useSessionContext } from '@providers/session/session_context'
import { i18WithParams as t } from '@shared/locale'
import { captureError } from '@shared/monitoring'
import { useRoutes } from 'app/use_routes'
import { ColorChip } from 'components-ui/src/color_chip/color_chip'
import { BenefitConsumptionTable, RequestOptions } from 'components-ui/src/membership/benefit_consumption_table'
import { BenefitsList } from 'components-ui/src/membership/benefits_list'
import { isUnlimitedBenefitsOnly } from 'components-ui/src/membership/helper'
import { formatNumberAsPrice } from 'components-ui/src/shared/common'
import { PRICE_PER_CYCLE_TEXT } from 'components-ui/src/shared/constants'
import { DEFAULT_TIMEZONE, getTzDateInUtc } from 'components-ui/src/shared/dates'
import { BillingCycle, membershipCycle } from 'components-ui/src/shared/memberships'
import { Spacer } from 'components-ui/src/spacer/spacer'
import { useClientBenefitConsumptionRecords } from 'service-api/src/hooks/use_client_benefit_consumption_records'
import { useClientInvoiceFilterDates } from 'service-api/src/hooks/use_client_invoice_filter_dates'
import { useClientLocationUserProfile } from 'service-api/src/hooks/use_client_location_user_profiles'
import { ClientLocationUserProfileMembershipService } from 'service-api/src/services/client_location_user_profile_memberships.service'
import {
  BenefitConsumptionRecordAdminDto,
  BenefitConsumptionRecordClientDto,
  LocationUserProfileMembershipClientDto,
} from 'service-api/src/types/api'

export const Memberships = () => {
  const sessionCtx = useSessionContext()
  const { buildPath } = useRoutes()
  const { accountName: locationName = '' } = useParams()
  const [lupMembership, setLupMembership] = useState<LocationUserProfileMembershipClientDto | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const theme = useTheme()
  const isBelowMD = useMediaQuery(theme.breakpoints.down('md'))
  const lupId = sessionCtx.state.user?.lupId

  const { location } = useLocationContext()

  const lup = useClientLocationUserProfile(locationName, lupId || '', [
    'locationUserProfileMemberships.locationMembership.membership',
  ])

  const fetchMemberships = useCallback(
    async (id: string) => {
      try {
        setIsLoading(true)
        if (id) {
          const result = await ClientLocationUserProfileMembershipService.get(locationName, id, [
            'locationMembership.membership',
            'locationMembership.prices',
            'locationMembership.benefits.benefitOffers.offer',
          ])

          if (result) {
            setLupMembership(result)
          }
        }
        setIsLoading(false)
      } catch (error) {
        setIsLoading(false)
        captureError(error as Error)
      }
    },
    [locationName]
  )

  const { data: filterDates } = useClientInvoiceFilterDates(locationName, lupId || '')

  const [requestOptions, setRequestOptions] = useState<RequestOptions>({ enabled: false })
  const [nextToken, setNextToken] = useState<string | null>(null)
  const [isInitialLoaded, setIsInitialLoaded] = useState(false)
  const [benefitConsumptionRecords, setBenefitConsumptionRecords] = useState<BenefitConsumptionRecordClientDto[]>([])

  const { data: benefitConsumptionRecordsData, isLoading: isLoadingRecords } = useClientBenefitConsumptionRecords(
    locationName,
    lupId || '',
    requestOptions
  )

  const handleOptionsChange = useCallback(
    (options: RequestOptions) => {
      setRequestOptions({ ...options, enabled: isInitialLoaded })
      setBenefitConsumptionRecords([])
      setNextToken(null)
    },
    [isInitialLoaded]
  )

  const handleLoadMore = useCallback(() => {
    if (nextToken) {
      setRequestOptions((prevOptions) => ({ ...prevOptions, nextToken }))
    }
  }, [nextToken])

  useEffect(() => {
    if (benefitConsumptionRecordsData?.results) {
      if (benefitConsumptionRecordsData.results.length > 0) {
        if (requestOptions.nextToken) {
          setBenefitConsumptionRecords((prev) => [...prev, ...benefitConsumptionRecordsData.results])
        } else {
          setBenefitConsumptionRecords(benefitConsumptionRecordsData.results)
        }
      }
      setNextToken(benefitConsumptionRecordsData.nextToken || null)
    }
  }, [benefitConsumptionRecordsData, requestOptions.nextToken])

  useEffect(() => {
    if (!lup.isLoading) {
      fetchMemberships(lup.data?.locationUserProfileMemberships?.[0]?.id || '')
    }
  }, [fetchMemberships, lup.data?.locationUserProfileMemberships, lup.isLoading])

  const timezone = location?.timezone || DEFAULT_TIMEZONE
  const { locationMembership, startDate, nextActionDate } = lupMembership || {}
  const { startDate: membershipCycleStartDate, endDate: membershipCycleEndDate } = membershipCycle(
    lupMembership,
    locationMembership?.membership?.billingCycle,
    timezone
  )

  const renderMembership = (lupmembership: LocationUserProfileMembershipClientDto | null) => {
    if (!lupmembership || lupmembership?.subStatus === 'CANCELED') {
      return (
        <Paper elevation={1} sx={{ p: 2 }}>
          <Typography variant="h5" color="text.secondaryShade60">
            {t('profile.membership.noMembership')}
          </Typography>
        </Paper>
      )
    }
    const startDateUTC = getTzDateInUtc(startDate, null, timezone)
    const nextActionDateUTC = getTzDateInUtc(nextActionDate, null, timezone)

    return (
      <Stack spacing={2}>
        <Stack direction="row" spacing={2}>
          {nextActionDate && (
            <>
              <Paper elevation={0} sx={{ flex: 1.6, p: 2, backgroundColor: 'primary.shade8' }}>
                <Typography variant="body1bold" color="text.secondary">
                  {t('membership.currentMembershipCycle')}
                </Typography>
                <Spacer height={0.5} />
                <Typography variant="h6">
                  {t('global.label.dateRange', {
                    startDate: formatInTimeZone(membershipCycleStartDate, timezone, 'MMM d, yyyy'),
                    endDate: formatInTimeZone(membershipCycleEndDate, timezone, 'MMM d, yyyy'),
                  })}
                </Typography>
              </Paper>
              <Paper elevation={0} sx={{ flex: 1, p: 2, backgroundColor: 'primary.shade8' }}>
                <Typography variant="body1bold" color="text.secondary">
                  {t('membership.nextPaymentDate')}
                </Typography>
                <Spacer height={0.5} />
                <Typography variant="h6">{formatInTimeZone(nextActionDateUTC, timezone, 'MMM d, yyyy')}</Typography>
              </Paper>
            </>
          )}
          {startDate && (
            <Paper elevation={0} sx={{ flex: 1, p: 2, backgroundColor: 'primary.shade8' }}>
              <Typography variant="body1bold" color="text.secondary">
                {t('membership.memberSince')}
              </Typography>
              <Spacer height={0.5} />
              <Typography variant="h6">{formatInTimeZone(startDateUTC, timezone, 'MMMM yyyy')}</Typography>
            </Paper>
          )}
        </Stack>
        <Paper key={lupmembership?.id} elevation={1} sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between">
            <Stack direction="row" alignItems="center">
              <Typography variant="h5">{locationMembership?.membership?.displayName},&nbsp;</Typography>
              <Typography variant="h5" color="text.secondaryShade60">
                {locationMembership?.membership?.billingCycle &&
                  t(PRICE_PER_CYCLE_TEXT[locationMembership?.membership?.billingCycle as BillingCycle], {
                    price: formatNumberAsPrice(locationMembership?.prices?.[0]?.value || 0),
                  })}
              </Typography>
            </Stack>
            <ColorChip
              label={t('global.label.active').toUpperCase()}
              textColor={theme.palette.success.main}
              bgColor={theme.palette.success.shade12}
              sxOverrides={{ borderRadius: 1, fontWeight: 600 }}
            />
          </Stack>
          <Spacer height={1} />
          <BenefitsList benefits={locationMembership?.benefits || []} />
        </Paper>
      </Stack>
    )
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Notification lupId={sessionCtx.state.user?.lupId || ''} />

      <Typography variant="h2" pl={isBelowMD ? 2 : 0}>
        {t('profile.membership.title')}
      </Typography>
      <Spacer height={2} />
      <Typography variant="body1bold" color="text.secondary" pl={isBelowMD ? 2 : 0}>
        {t('profile.membership.subTitle')}
      </Typography>
      <Spacer height={2} />
      {lup.isLoading || isLoading ? (
        <Skeleton variant="rectangular" width="100%" height={200} />
      ) : (
        <>
          {renderMembership(lupMembership)}
          {lupMembership?.subStatus === 'CANCELED' ||
          !isUnlimitedBenefitsOnly(lupMembership?.locationMembership?.benefits || []) ? (
            <Paper elevation={1} sx={{ mt: 2 }}>
              <BenefitConsumptionTable
                locationUserProfileMembership={lupMembership}
                timezone={location?.timezone || DEFAULT_TIMEZONE}
                data={benefitConsumptionRecords}
                isLoading={isLoadingRecords}
                onOptionsChange={handleOptionsChange}
                onLoadMore={handleLoadMore}
                nextToken={nextToken}
                invoicePath={(benefitConsumptionRecord: BenefitConsumptionRecordAdminDto) => {
                  return buildPath('purchaseSummary', {
                    bookingId: benefitConsumptionRecord.invoiceItem?.invoice?.orderId || '',
                  })
                }}
                invoiceFilterDates={filterDates}
                inlineFilters
                setIsInitialLoaded={setIsInitialLoaded}
                isInitialLoaded={isInitialLoaded}
              />
            </Paper>
          ) : null}
        </>
      )}
    </Box>
  )
}
